import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  fetchNotifications,
  markNotificationSeen,
  markAllNotificationsSeen,
} from '../lib/api/notifications';

const useNotifications = (pollIntervalMs = 10000) => {
  const { user, loading: authLoading } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      return [];
    }

    setLoading(true);
    try {
      const data = await fetchNotifications({ limit: 50 });
      if (isMountedRef.current) {
        setNotifications(Array.isArray(data) ? data : []);
      }
      return data;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [user]);

  useEffect(() => {
    isMountedRef.current = true;
    if (!user) {
      setNotifications([]);
      return undefined;
    }

    refresh();

    if (!pollIntervalMs || pollIntervalMs <= 0) {
      return () => {
        isMountedRef.current = false;
      };
    }

    const timer = setInterval(() => {
      refresh();
    }, pollIntervalMs);

    return () => {
      clearInterval(timer);
      isMountedRef.current = false;
    };
  }, [pollIntervalMs, refresh, user]);

  const markAsSeen = useCallback(async (notificationId) => {
    if (!notificationId) {
      return;
    }
    try {
      await markNotificationSeen(notificationId, true);
    } finally {
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? {
                ...notification,
                seen: true,
                seenAt:
                  notification.seenAt ?? new Date().toISOString(),
              }
            : notification,
        ),
      );
    }
  }, []);

  const markAllAsSeen = useCallback(async () => {
    try {
      await markAllNotificationsSeen();
    } finally {
      setNotifications((prev) =>
        prev.map((notification) => ({
          ...notification,
          seen: true,
          seenAt: notification.seenAt ?? new Date().toISOString(),
        })),
      );
    }
  }, []);

  const unseenCount = useMemo(
    () => notifications.filter((notification) => !notification?.seen).length,
    [notifications],
  );

  return {
    notifications,
    unseenCount,
    loading: loading || authLoading,
    refresh,
    markAsSeen,
    markAllAsSeen,
    ready: Boolean(user) && !authLoading,
  };
};

export default useNotifications;
